import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

export interface NavigationState {
    currentRoute: string
    openModals: string[]
    pageStates: {
        [route: string]: {
            activeTab?: string
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            filters?: Record<string, any>
            scrollPosition?: number
            selectedItems?: string[]
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            customData?: Record<string, any>
        }
    }
    navigationHistory: string[]
    timestamp: string
}

const DEFAULT_STATE: NavigationState = {
    currentRoute: '',
    openModals: [],
    pageStates: {},
    navigationHistory: [],
    timestamp: new Date().toISOString()
}

export function useNavigationState(user?: { id: string } | null) {
    const [navState, setNavState] = useState<NavigationState>(DEFAULT_STATE)
    const [isLoaded, setIsLoaded] = useState(false)
    const saveTimeoutRef = useRef<number | undefined>(undefined)
    const isRemoteUpdate = useRef(false)

    // Load navigation state from database
    useEffect(() => {
        async function loadNavigationState() {
            if (!user) {
                setIsLoaded(true)
                return
            }

            const { data, error } = await supabase
                .from('user_preferences')
                .select('navigation_state')
                .eq('user_id', user.id)
                .single()

            if (error) {
                console.error('Error loading navigation state:', error)
                setIsLoaded(true)
                return
            }

            console.log('🔍 DEBUG: Loaded from DB:', data?.navigation_state)

            if (data?.navigation_state && typeof data.navigation_state === 'object') {
                // Safely convert Json type to NavigationState
                const loadedState = data.navigation_state as unknown as NavigationState
                // IMPORTANT: Ignore the persisted currentRoute - browser URL is the source of truth
                const stateToApply = {
                    ...loadedState,
                    currentRoute: '' // Always use empty string, never restore persisted route
                }
                console.log('✅ DEBUG: Setting nav state:', stateToApply)
                setNavState(stateToApply)
            }
            setIsLoaded(true)
        }

        if (user && !isLoaded) {
            loadNavigationState()
        }
    }, [user, isLoaded])

    // Realtime subscription for cross-device sync
    useEffect(() => {
        if (!user) return

        const channel = supabase
            .channel('navigation_sync')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'user_preferences',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    const newState = payload.new.navigation_state as unknown as NavigationState

                    if (newState && typeof newState === 'object') {
                        setNavState(prevState => {
                            // Conflict resolution: Last Write Wins based on timestamp
                            const remoteTime = newState.timestamp ? new Date(newState.timestamp).getTime() : 0
                            const localTime = prevState.timestamp ? new Date(prevState.timestamp).getTime() : 0

                            // Only update if remote state is newer
                            if (remoteTime > localTime) {
                                isRemoteUpdate.current = true
                                return newState
                            }
                            return prevState
                        })
                    }
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [user])

    // Debounced save to database
    const saveToDatabase = useCallback(
        async (state: NavigationState) => {
            if (!user) return

            const stateWithTimestamp = {
                ...state,
                timestamp: new Date().toISOString()
            }

            console.log('💾 DEBUG: Saving to DB:', stateWithTimestamp)

            const { error } = await supabase
                .from('user_preferences')
                .upsert({
                    user_id: user.id,
                    navigation_state: stateWithTimestamp
                }, {
                    onConflict: 'user_id'
                })

            if (error) {
                console.error('Error saving navigation state:', error)
            } else {
                console.log('✅ DEBUG: Saved successfully!')
            }
        },
        [user]
    )

    // Debounced save (500ms)
    useEffect(() => {
        console.log('🔄 DEBUG: Save useEffect triggered. isLoaded:', isLoaded, 'navState:', navState)

        if (!isLoaded) return

        // Skip saving if this was a remote update
        if (isRemoteUpdate.current) {
            isRemoteUpdate.current = false
            return
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current)
        }

        saveTimeoutRef.current = setTimeout(() => {
            // Use functional setState to get the LATEST state at save time
            setNavState(currentState => {
                saveToDatabase(currentState)
                return currentState // Don't modify state, just trigger save
            })
        }, 500)

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current)
            }
        }
    }, [navState, isLoaded, saveToDatabase])

    // Set current route
    const setCurrentRoute = useCallback((route: string) => {
        setNavState(prev => ({
            ...prev,
            currentRoute: route,
            navigationHistory: [...prev.navigationHistory.slice(-9), route]
        }))
    }, [])

    // Open modal
    const openModal = useCallback((modalId: string) => {
        console.log('📂 DEBUG: openModal called with:', modalId)
        setNavState(prev => {
            const newState = {
                ...prev,
                openModals: [...new Set([...prev.openModals, modalId])],
                timestamp: new Date().toISOString()
            }
            console.log('✨ DEBUG: New state created with openModals:', newState.openModals)

            // Save IMMEDIATELY - no debounce!
            saveToDatabase(newState)

            return newState
        })
    }, [saveToDatabase])

    // Close modal
    const closeModal = useCallback((modalId: string) => {
        console.log('❌ DEBUG: closeModal called with:', modalId)
        setNavState(prev => {
            const newState = {
                ...prev,
                openModals: prev.openModals.filter(id => id !== modalId),
                timestamp: new Date().toISOString()
            }

            // Save IMMEDIATELY - no debounce!
            saveToDatabase(newState)

            return newState
        })
    }, [saveToDatabase])

    // Get page state
    const getPageState = useCallback((route: string) => {
        return navState.pageStates[route]
    }, [navState.pageStates])

    // Set page state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setPageState = useCallback((route: string, state: any) => {
        setNavState(prev => ({
            ...prev,
            pageStates: {
                ...prev.pageStates,
                [route]: state
            }
        }))
    }, [])

    // Clear all state (on logout)
    const clearNavigationState = useCallback(() => {
        setNavState(DEFAULT_STATE)
    }, [])

    return {
        currentRoute: navState.currentRoute,
        openModals: navState.openModals,
        navigationHistory: navState.navigationHistory,
        isLoaded,
        setCurrentRoute,
        openModal,
        closeModal,
        getPageState,
        setPageState,
        clearNavigationState
    }
}
